import React, { useState, useCallback, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { subMonths, eachDayOfInterval, format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import Switch from 'react-switch';  // react-switch 사용
import config from '../../../config';
import './GenDaily.css';  // 스타일 파일 추가
import { CSVLink } from 'react-csv';  // CSVLink 가져오기
import { ko } from 'date-fns/locale';
import { fetchEv_list } from '../../../store';
import { parseEvSensorData  } from '../../../utils/parseEvSensorData ';
import useIsMobile from '../../../hooks/useIsMobile';

// [사용자 입력]
//    ↓
// 날짜 + 보기 옵션 선택
//    ↓
// [검색 버튼 클릭]
//    ↓
// 백엔드에서 날짜 범위의 발전량 데이터를 가져옴
//    ↓
// 각 날짜마다 발전량 계산 (전체 or 개별)
//    ↓
// 발전시간도 계산
//    ↓
// 선택한 모양대로 렌더링 (차트 또는 표)


// 고유 색상 생성 함수 (HSL을 이용하여 유사한 색상을 피함)
const generateColor = (index, total) => {
    const hue = Math.floor((360 / total) * index); // 색상 범위(0-360도) 내에서 인덱스에 따라 고유한 hue 생성
    return `hsl(${hue}, 70%, 50%)`; // 채도와 밝기를 고정하여 고유한 색상 반환
};

const DailyGeneration = () => {
    const dispatch = useDispatch();
    const ivList = useSelector(state => state.Iv_list);  // Redux에서 인버터 리스트 불러오기
    const Ev_list = useSelector((state) => state.Ev_list || {});
    
    // 보기 설정 상태들
    const [viewMode, setViewMode] = useState('total');
    const [displayType, setDisplayType] = useState('chart');

    const [tempViewMode, setTempViewMode] = useState('total');
    const [tempDisplayType, setTempDisplayType] = useState('chart');
    // 날짜 설정
    const [startDate, setStartDate] = useState(subMonths(new Date(), 1));
    const [endDate, setEndDate] = useState(new Date());
    // 시각화 및 CSV용 상태
    const [displayedData, setDisplayedData] = useState(null);
    const [csvData, setCsvData] = useState([]);  // CSV 데이터 저장
    const [lastSearchMode, setLastSearchMode] = useState('total');

    useEffect(() => {
          dispatch(fetchEv_list());
        }, [dispatch]);

    // totalCapacity 정의 (인버터 용량의 합계)
    const totalCapacity = ivList.iv_capa.reduce((sum, capa) => sum + capa, 0); // 각 인버터의 용량을 합산
    
    // 종료일 변경 시 시작일 자동 보정
    const handleEndDateChange = (date) => {
        setEndDate(date);
        const newStartDate = subMonths(date, 3); // 종료일 기준 3개월 전
        if (startDate < newStartDate) {
            setStartDate(newStartDate); // 시작일이 3개월보다 이전이면 조정
        }
    };

    // iv_add_power:일간 누적 전력량
    // 검색 버튼 클릭 시 데이터 조회
    const handleSearch = useCallback(async () => {
        try {
            // 날짜 포맷 한번만 처리
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');

            // 요청 바디 공통
            const body = JSON.stringify({ start_date: startDateStr, end_date: endDateStr });

            // 병렬 요청: 인버터 + 환경센서
            const [ivRes, envRes] = await Promise.all([
                fetch(`${config.backendUrl}/get_day_Iv_data`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                }),
                fetch(`${config.backendUrl}/get_hourly_Ev_mrt_data`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                }),
            ]);

            // 응답 체크
            if (!ivRes.ok || !envRes.ok) {
                throw new Error(`요청 실패: IV ${ivRes.status}, ENV ${envRes.status}`);
            }

            // 데이터 파싱
            const data = await ivRes.json();
            const envData = await envRes.json();
    
                
            // startDate부터 endDate까지 모든 날짜를 'yyyy-MM-dd' 형식의 문자열 배열로 생성
            const allDates = eachDayOfInterval({
                start: new Date(startDate),
                end: new Date(endDate),
            }).map(date => format(date, 'yyyy-MM-dd'));

            let formattedCsvData = [];

            // 여기서 전체보기/ 개별보기 나눠서 파싱 -> 렌더링 .. 인버터는 이미 다 되어있고 환경센서를 이 로직에 녹아들게 하는게 가장 오류가 적을 것 같다.. 환경센서 
            // 데이터는 가져왔고, 하루 데이터 평균, 계산해서 돌려주기
            
            //환경센서
            const { horizontalIrradiance, tiltedIrradiance, surfaceTemp, outdoorTemp } =
                parseEvSensorData(envData, allDates, Ev_list);

            if (tempViewMode === 'total') {
                // 전체 합계 보기 모드
                // 날짜별 데이터 총 합 구하기
                const totalData = allDates.map(date => {
                    return ivList.seq.reduce((sum, seq) => {
                        const entry = data.data.find(item => item.seq === seq && item.created_at.split(' ')[0] === date);
                        return sum + (entry ? entry.Iv_Add_power : 0);
                    }, 0);
                });
                // 발전시간 계산
                const totalGenerationHours = totalData.reduce((sum, value) => sum + value, 0) / totalCapacity / 1000;

                // 차트 데이터 설정
                setDisplayedData({
                    labels: allDates,
                    datasets: [
                        {
                        label: '전체 합계',
                        data: totalData,
                        backgroundColor: 'rgba(255, 99, 132, 0.6)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1,
                        },
                        {
                        label: '수평 일사량',
                        data: horizontalIrradiance,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                        },
                        {
                        label: '경사 일사량',
                        data: tiltedIrradiance,
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                        },
                        {
                        label: '외기 온도',
                        data: outdoorTemp,
                        borderColor: 'rgba(255, 206, 86, 1)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                        },
                        {
                        label: '표면 온도',
                        data: surfaceTemp,
                        borderColor: 'rgba(153, 102, 255, 1)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                        }
                    ],
                });

                // CSV 데이터 형식화 (전체 합계용)
                formattedCsvData = [
                    [
                        '날짜',
                        '전체 합계 (kW)',
                        '발전시간 (h)',
                        '수평 일사량 (W/m²)',
                        '경사 일사량 (W/m²)',
                        '모듈 온도 (℃)',
                        '외기 온도 (℃)'
                    ],
                    ...allDates.map((date, idx) => [
                        date,
                        (totalData[idx] / 1000).toFixed(2) + ' kW',
                        (totalData[idx] / totalCapacity / 1000).toFixed(2) + ' h',
                        (horizontalIrradiance[idx] !== null && horizontalIrradiance[idx] !== undefined
                          ? horizontalIrradiance[idx].toFixed(2) + ' W/m²'
                          : ''),

                        (tiltedIrradiance[idx] !== null && tiltedIrradiance[idx] !== undefined
                          ? tiltedIrradiance[idx].toFixed(2) + ' W/m²'
                          : ''),
                        surfaceTemp[idx]?.toFixed(1) + ' ℃' ?? '',
                        outdoorTemp[idx]?.toFixed(1) + ' ℃' ?? ''
                    ])
                ];

            } else {
                // 개별 보기 모드
                const totalInverters = ivList.seq.length;
                // 인버터별 데이터셋 생성
                const inverterDatasets = ivList.seq.map((seq, index) => {
                    const ivData = data.data.filter(item => item.seq === seq);
                    const rowData = allDates.map(label => {
                        const entry = ivData.find(item => item.created_at.split(' ')[0] === label);
                        return entry ? entry.Iv_Add_power : 0;
                    });

                    return {
                        label: ivList.iv_name[index],
                        data: rowData,
                        backgroundColor: generateColor(index, totalInverters),
                        borderColor: generateColor(index, totalInverters),
                        borderWidth: 2,
                        fill: false,
                        yAxisID: 'y',
                    };
                });

                // 🟡 환경센서용 데이터셋 (Y1 축에 매핑)
                const envSensorDatasets = [
                    {
                        label: '수평 일사량',
                        data: horizontalIrradiance,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        backgroundColor: 'rgba(54, 162, 235, 0.3)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                    },
                    {
                        label: '경사 일사량',
                        data: tiltedIrradiance,
                        borderColor: 'rgba(75, 192, 192, 1)',
                        backgroundColor: 'rgba(75, 192, 192, 0.3)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                    },
                    {
                        label: '외기 온도',
                        data: outdoorTemp,
                        borderColor: 'rgba(255, 206, 86, 1)',
                        backgroundColor: 'rgba(255, 206, 86, 0.3)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                    },
                    {
                        label: '표면 온도',
                        data: surfaceTemp,
                        borderColor: 'rgba(153, 102, 255, 1)',
                        backgroundColor: 'rgba(153, 102, 255, 0.3)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                    },
                ];


                setDisplayedData({
                    labels: allDates,
                    datasets: [...inverterDatasets, ...envSensorDatasets],
                });

                const totalGenerationHours = inverterDatasets.reduce((sum, dataset) => {
                    return sum + dataset.data.reduce((innerSum, value) => innerSum + value, 0);
                }, 0) / totalCapacity / 1000;

                // CSV 데이터 생성
                formattedCsvData = [
                    [
                        '날짜',
                        ...ivList.iv_name.map(name => `${name} (kW)`),
                        '발전시간 (h)',
                        '합계 (kW)',
                        '수평 일사량 (W/m²)',
                        '경사 일사량 (W/m²)',
                        '모듈 온도 (℃)',
                        '외기 온도 (℃)'
                    ],
                    ...allDates.map((date, idx) => {
                        const rowSum = inverterDatasets.reduce((sum, dataset) => sum + dataset.data[idx], 0);
                        return [
                            date,
                            ...inverterDatasets.map(dataset => (dataset.data[idx] / 1000).toFixed(2)),
                            (rowSum / totalCapacity / 1000).toFixed(2) + ' KW',
                            (rowSum / 1000).toFixed(2),
                            (horizontalIrradiance[idx] !== null && horizontalIrradiance[idx] !== undefined
                              ? horizontalIrradiance[idx].toFixed(2) + ' W/m²'
                              : ''),

                            (tiltedIrradiance[idx] !== null && tiltedIrradiance[idx] !== undefined
                              ? tiltedIrradiance[idx].toFixed(2) + ' W/m²'
                              : ''),
                            surfaceTemp[idx] != null ? surfaceTemp[idx].toFixed(1) + ' ℃' : '',
                            outdoorTemp[idx] != null ? outdoorTemp[idx].toFixed(1) + ' ℃' : ''
                        ];
                    }),
                    [
                        '합계',
                        ...inverterDatasets.map(dataset =>
                            (dataset.data.reduce((sum, value) => sum + value, 0) / 1000).toFixed(2)
                        ),
                        totalGenerationHours.toFixed(2),
                        (inverterDatasets.reduce((sum, dataset) => sum + dataset.data.reduce((innerSum, value) => innerSum + value, 0), 0) / 1000).toFixed(2),
                        // 누적 일사량/온도 (필터링 포함)
                        horizontalIrradiance.filter(v => typeof v === 'number' && !isNaN(v)).reduce((a, b) => a + b, 0).toFixed(0),
                        tiltedIrradiance.filter(v => typeof v === 'number' && !isNaN(v)).reduce((a, b) => a + b, 0).toFixed(0),
                        surfaceTemp.filter(v => typeof v === 'number' && !isNaN(v)).reduce((a, b) => a + b, 0).toFixed(1) + ' ℃',
                        outdoorTemp.filter(v => typeof v === 'number' && !isNaN(v)).reduce((a, b) => a + b, 0).toFixed(1) + ' ℃'

                    ]
                ];
            }

            // 상태 업데이트
            setCsvData(formattedCsvData);
            setLastSearchMode(tempViewMode);
            setViewMode(tempViewMode);
            setDisplayType(tempDisplayType);

        } catch (error) {
            console.error(error);
            alert('데이터를 가져오는 중 오류가 발생했습니다.');
            setDisplayedData(null);
        }
    }, [startDate, endDate, ivList, tempViewMode, tempDisplayType, totalCapacity]);

    return (
    <div>
      {/* 날짜 선택 및 검색 버튼 */}
      <div className="controls-container">
        {/* 날짜 선택 */}
        <div className="date-picker-group">
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
            maxDate={endDate}
            minDate={subMonths(endDate, 3)}
            locale={ko}
            customInput={<input className="date-input" />}
          />
          <div>~</div>
          <DatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            dateFormat="yyyy-MM-dd"
            maxDate={new Date()}
            locale={ko}
            customInput={<input className="date-input" />}
          />
        </div>

        {/* 검색 버튼 */}
        <button onClick={handleSearch} className="styled-button">검색</button>

        {/* 전체 합계 / 개별 보기 슬라이드 버튼 */}
       
            <div className="toggle-container">
                <span className="toggle-label">개별 보기</span>
                <Switch
                    className="daily-toggle-switch"
                    onChange={() => setTempViewMode(tempViewMode === "individual" ? "total" : "individual")}
                    checked={tempViewMode === "total"}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onColor="#86d3ff"
                    offColor="#86d3ff"
                    onHandleColor="#0D6EFD"
                    offHandleColor="#0D6EFD"
                />
                <span className="toggle-label">전체 합계</span>
            </div>

            {/* 그래프 / 표 보기 슬라이드 버튼 */}
            <div className="toggle-container">
                <span className="toggle-label">표 보기</span>
                <Switch
                    onChange={() => setTempDisplayType(tempDisplayType === "table" ? "chart" : "table")}
                    checked={tempDisplayType === "chart"}
                    className="react-switch"
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onColor="#86d3ff"
                    offColor="#86d3ff"
                    onHandleColor="#0D6EFD"
                    offHandleColor="#0D6EFD"
                />
                <span className="toggle-label">그래프 보기</span>
            </div>
    
        {/* CSV 다운로드 버튼 */}
        <CSVLink data={csvData} filename="daily-generation-data.csv" className="csv-download">
          CSV 다운로드
        </CSVLink>
      </div>

      {/* ChartComponent 또는 TableComponent 렌더링 */}
      {displayType === "chart" ? (
        <ChartComponent displayedData={displayedData} lastSearchMode={lastSearchMode} />
      ) : (
        <TableComponent displayedData={displayedData} viewMode={viewMode} ivList={ivList} />
      )}
    </div>
  );
};




// 그래프 컴포넌트 
const ChartComponent = ({ displayedData, lastSearchMode }) => {
    return (
        <div className="chartHour-chart-container" style={{width: "90%"}}>
            {displayedData && displayedData.labels && displayedData.datasets ? (
                lastSearchMode === 'total' ? (
                    <Bar
                        data={displayedData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    position: 'top',
                                },
                               tooltip: {
                                callbacks: {
                                  label: function (tooltipItem) {
                                    const label = tooltipItem.dataset.label;
                                    const value = tooltipItem.raw;

                                    let unit = '';
                                    let displayValue = value;

                                    // 단위 조건 분기
                                    if (label.includes('일사량')) {
                                      unit = 'W/m²';
                                      displayValue = value; // 그대로
                                    } else if (label.includes('온도')) {
                                      unit = '℃';
                                      displayValue = value; // 그대로
                                    } else {
                                      unit = 'kW';
                                      displayValue = value / 1000; 
                                    }

                                    return `${label}: ${displayValue.toFixed(2)} ${unit}`;
                                  },
                                },
                              },
                            },
                            scales: {
                                x: {
                                    title: {
                                        display: true,
                                    },
                                },
                                y: {
                                    type: 'linear',
                                    position: 'left',
                                    title: {
                                    display: true,
                                    text: '발전량 (kW)',
                                    },
                                    ticks: {
                                    callback: function (value) {
                                        return value / 1000 + ' kW';  // 발전량 단위 변환
                                    },
                                    },
                                },
                                y1: {
                                    type: 'linear',
                                    position: 'right',
                                    title: {
                                    display: true,
                                    text: '환경센서 (일사량 / 온도)',
                                    },
                                    grid: {
                                    drawOnChartArea: false,  // 오른쪽 축의 격자선 안 보이게
                                    },
                                    ticks: {
                                    callback: function (value) {
                                        return value;  // 단위가 다를 수 있으니 그대로 표시
                                    },
                                    },
                                },
                            },
                        }}
                        width={800}
                        height={400}
                    />
                ) : (
                    <Line
                        data={displayedData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    position: 'top',
                                },
                                tooltip: {
                                    callbacks: {
                                        label: function (tooltipItem) {
                                        const label = tooltipItem.dataset.label;
                                        const value = tooltipItem.raw;

                                        let unit = 'kW';
                                        let displayValue = value / 1000;

                                        if (label.includes('일사량')) {
                                            unit = 'W/m²';
                                            displayValue = value;
                                        } else if (label.includes('온도')) {
                                            unit = '℃';
                                            displayValue = value;
                                        }  

                                        return `${label}: ${displayValue.toFixed(2)} ${unit}`;
                                        },
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    title: {
                                        display: true,
                                    },
                                },
                                y: {
                                    type: 'linear',
                                    position: 'left',
                                    title: {
                                    display: true,
                                    text: '발전량 (kW)',
                                    },
                                    ticks: {
                                    callback: function (value) {
                                        return value / 1000 + ' kW';
                                    },
                                    },
                                },
                                y1: {
                                    type: 'linear',
                                    position: 'right',
                                    title: {
                                    display: true,
                                    text: '환경센서 (일사량 / 온도)',
                                    },
                                    grid: {
                                    drawOnChartArea: false,  // 오른쪽 격자선 제거
                                    },
                                    ticks: {
                                    callback: function (value) {
                                        return value;  // 필요시 단위 추가 가능
                                    },
                                    },
                                },
                            },
                            elements: {
                                line: {
                                    tension: 0.4,
                                },
                                point: {
                                    radius: 2,
                                    hoverRadius: 5,
                                },
                            },
                        }}
                        width={800}
                        height={400}
                    />
                )
            ) : (
                <p style={{ textAlign: 'center' }}>날짜를 선택하고 검색을 눌러주세요.</p>
            )}
        </div>
    );
};


// 표 컴포넌트
const TableComponent = ({ displayedData, viewMode, ivList }) => {
  if (!displayedData || !displayedData.labels) {
    return <p>데이터가 없습니다.</p>;
  }

  // 라벨 기반 타입 판별
  const isIrr = (label = "") => label.includes("일사량"); // W/m²
  const isTemp = (label = "") => label.includes("온도");   // ℃
  const isEnergy = (label = "") => !(isIrr(label) || isTemp(label)); // Wh

  // 단위 헤더/셀 포맷터
  const headerUnit = (label) => {
    if (isIrr(label)) return "(W/m²)";
    if (isTemp(label)) return "(℃)";
    return "(kWh)"; // 에너지
  };

  const formatCell = (label, value) => {
    const v = value ?? 0;
    if (isIrr(label)) return `${Number(v).toFixed(0)} W/m²`;      // 정수/그대로
    if (isTemp(label)) return `${Number(v).toFixed(1)} ℃`;        // 소수1
    return `${(Number(v) / 1000).toFixed(2)} kWh`;                // Wh → kWh
  };

  // 전체 용량 합계(kW)
  const totalCapacity = ivList.iv_capa.reduce((sum, capa) => sum + (capa || 0), 0);

  // 에너지(Wh) 데이터만 합산해서 행 합계/발전시간 계산
  const rowSumsWh = displayedData.labels.map((_, rowIndex) =>
    displayedData.datasets.reduce((sum, ds) => {
      if (!isEnergy(ds.label)) return sum;
      const val = ds.data?.[rowIndex] ?? 0;
      return sum + val;
    }, 0)
  );

  // 각 행 발전시간(h) = (해당 날짜 총 Wh / 1000) / 총 kW
  const rowGenerationHours = rowSumsWh.map((sumWh) =>
    totalCapacity > 0 ? (sumWh / 1000) / totalCapacity : 0
  );

  // 컬럼 합계: 에너지(Wh)는 합계(kWh)로, 환경센서는 의미 없으니 공백으로 처리
  const columnSumsDisplay = displayedData.datasets.map((ds) => {
    if (!isEnergy(ds.label)) return ""; // 일사량/온도는 합계 안 찍음(원하면 평균 등으로 변경)
    const sumWh = ds.data.reduce((s, v) => s + (v ?? 0), 0);
    return `${(sumWh / 1000).toFixed(2)} kWh`;
  });

  // 전체 발전시간 합계(h)
  const totalGenerationHours =
    totalCapacity > 0
      ? rowSumsWh.reduce((s, v) => s + v, 0) / 1000 / totalCapacity
      : 0;

  // 개별 보기일 때만 “행 합계(kWh)” 표시
  const rowSumKWhDisplay = (rowIndex) =>
    `${(rowSumsWh[rowIndex] / 1000).toFixed(2)} kWh`;

  const grandTotalKWhDisplay = `${(
    rowSumsWh.reduce((s, v) => s + v, 0) / 1000
  ).toFixed(2)} kWh`;

  return (
    <div className="table-container">
      <table className="styled-table">
        <thead>
          <tr>
            <th>날짜</th>
            {displayedData.datasets.map((ds, idx) => (
              <th key={idx}>
                {ds.label} {headerUnit(ds.label)}
              </th>
            ))}
            <th>발전시간 (h)</th>
            {viewMode === "individual" && <th>합계 (kWh)</th>}
          </tr>
        </thead>
        <tbody>
          {displayedData.labels.map((label, rowIndex) => (
            <tr key={rowIndex}>
              <td>{label}</td>
              {displayedData.datasets.map((ds, colIndex) => (
                <td key={colIndex}>{formatCell(ds.label, ds.data?.[rowIndex])}</td>
              ))}
              <td>
                <strong>{rowGenerationHours[rowIndex].toFixed(2)} h</strong>
              </td>
              {viewMode === "individual" && (
                <td>
                  <strong>{rowSumKWhDisplay(rowIndex)}</strong>
                </td>
              )}
            </tr>
          ))}
          <tr className="total-row">
            <td>
              <strong>합계</strong>
            </td>
            {columnSumsDisplay.map((txt, idx) => (
              <td key={idx}>
                <strong>{txt}</strong>
              </td>
            ))}
            <td>
              <strong>{totalGenerationHours.toFixed(2)} h</strong>
            </td>
            {viewMode === "individual" && (
              <td>
                <strong>{grandTotalKWhDisplay}</strong>
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
  
export default DailyGeneration;
